import type { VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import { cn } from '@/lib/cn';

import { buttonVariants } from './buttonVariants';

/**
 * `DESIGN.md` §4. Four variants, two sizes, and one rule that is not a style preference:
 *
 * **`danger` is outlined in `negative` and never filled.** The warm accent sits near `negative` in
 * hue; a filled warm button is always the primary action and never a delete. That is what keeps
 * "Submit" and "Reject" from reading the same at a glance (§3).
 */
interface Props
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  children: ReactNode;
  /**
   * While in flight the button disables and swaps its label for a spinner **in place** — it never
   * disappears, so the layout does not jump and the user keeps the target they were aiming at
   * (§4 Forms).
   */
  isLoading?: boolean;
  /**
   * React 19 passes `ref` as an ordinary prop, so no `forwardRef` is needed — it is declared here
   * because a caller sometimes has to move focus onto the button (the shell returns focus to the
   * drawer toggle when the drawer closes, §9).
   */
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  children,
  className,
  variant,
  size,
  isLoading = false,
  disabled,
  type = 'button',
  ref,
  ...props
}: Props) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 size={16} strokeWidth={1.5} className="animate-spin" aria-hidden />
          <span className="sr-only">{children}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
